import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RowSkeleton } from "@/components/skeleton/RowSkeleton";
import type { AllowedUserInOrg } from "@/interfaces/user";
import { useDebounceState } from "@/hooks/useDebounceState";
import { useHasAdminFeatureAccess } from "@/hooks/useHasAdminFeatureAccess";
import AuthProvider from "@/providers/auth.provider";
import { appSelector } from "@/redux/selectors/app.selector";
import { useSelector } from "@/redux/store";
import { cn } from "@/lib/utils";
import { SearchInput } from "@/pages/dashboard/SearchInput";
import { createColumnHelper } from "@tanstack/react-table";
import { SETTINGS_TABLE_WRAPPER } from "@/pages/settings/settings-layout-constants";
import { SettingsTableToolbar } from "@/pages/settings/SettingsTableToolbar";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

const CUSTOMER_ADMIN_ASSIGNABLE_ROLES = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "Member" },
] as const;

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  admin: "Admin",
  user: "Member",
};

// Rebuild staff are members of nearly every prod org. This page is only reachable by customer org
// admins (Role.ADMIN — see useHasAdminFeatureAccess), who must not be able to view, edit, or remove
// Rebuild staff, so we hide those rows from the roster entirely. This is the UI guard against CRUD
// on Rebuild-internal users.
//
// Two ways an account is Rebuild staff:
//   1. role is internal/creator, OR
//   2. it has a @rebuild.work email. Many Rebuild team accounts inside customer orgs (e.g.
//      team+<org>@rebuild.work) are actually classified as admin/user — not internal — so the
//      role check alone misses them. Match the whole domain to be safe.
const REBUILD_STAFF_ROLES = new Set(["internal", "creator"]);
const REBUILD_TEAM_EMAIL = /@rebuild\.work$/i;
const isRebuildStaffUser = (account: { role?: string; email?: string }): boolean =>
  (!!account.role && REBUILD_STAFF_ROLES.has(account.role)) ||
  (!!account.email && REBUILD_TEAM_EMAIL.test(account.email));

const DEFAULT_TEAM_PAGE_SIZE = 25;

type ManageTeamTableRow = AllowedUserInOrg & { id: string };

function rosterMatchesQuery(row: AllowedUserInOrg, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim().toLowerCase();
  return (
    name.includes(q) ||
    (row.email ?? "").toLowerCase().includes(q) ||
    (row.role ?? "").toLowerCase().includes(q)
  );
}

const teamColumnHelper = createColumnHelper<ManageTeamTableRow>();

function buildTeamColumns({
  selfId,
  onEdit,
  onDelete,
}: {
  selfId: string | undefined;
  onEdit: (row: AllowedUserInOrg) => void;
  onDelete: (row: AllowedUserInOrg) => void;
}) {
  return [
    teamColumnHelper.display({
      id: "name",
      header: "Name",
      cell: ({ row }) => {
        const r = row.original;
        const label = (r.firstName || r.lastName ? `${r.firstName} ${r.lastName}`.trim() : "—") || "—";
        return <span className="block min-w-0 truncate font-medium">{label}</span>;
      },
      meta: { columnClassName: "w-[22%] min-w-0" },
    }),
    teamColumnHelper.accessor("email", {
      header: "Email",
      cell: (info) => (
        <span className="text-muted-foreground block min-w-0 truncate text-sm font-medium">{info.getValue()}</span>
      ),
      meta: { columnClassName: "w-[38%] min-w-0" },
    }),
    teamColumnHelper.accessor("role", {
      header: "Role",
      cell: (info) => {
        const role = info.getValue();
        return <span className="block min-w-0 truncate">{ROLE_DISPLAY_NAMES[role] ?? role}</span>;
      },
      meta: { columnClassName: "w-[12%] min-w-0" },
    }),
    teamColumnHelper.display({
      id: "status",
      header: "Status",
      cell: ({ row }) =>
        row.original.isRegistered ? (
          <Badge variant="secondary">Active</Badge>
        ) : (
          <Badge variant="outline">Invited</Badge>
        ),
      meta: { columnClassName: "w-[13%] min-w-0 whitespace-nowrap" },
    }),
    teamColumnHelper.display({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="flex gap-1">
            <Button type="button" variant="ghost" size="icon" aria-label="Edit user" onClick={() => onEdit(r)}>
              <Pencil className="size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remove user"
              disabled={r.lassoUserId === selfId}
              onClick={() => onDelete(r)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        );
      },
      meta: { columnClassName: "w-[15%] min-w-0" },
    }),
  ];
}

function UserDetailsFields({
  idPrefix,
  firstName,
  lastName,
  role,
  onChangeFirstName,
  onChangeLastName,
  onChangeRole,
  roleDisabled = false,
  children,
}: {
  idPrefix: string;
  firstName: string;
  lastName: string;
  role: string;
  onChangeFirstName: (value: string) => void;
  onChangeLastName: (value: string) => void;
  onChangeRole: (value: string) => void;
  roleDisabled?: boolean;
  children?: ReactNode;
}) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${idPrefix}-first`}>First name</Label>
          <Input id={`${idPrefix}-first`} value={firstName} onChange={(e) => onChangeFirstName(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${idPrefix}-last`}>Last name</Label>
          <Input id={`${idPrefix}-last`} value={lastName} onChange={(e) => onChangeLastName(e.target.value)} />
        </div>
      </div>
      {children}
      <div className="flex flex-col gap-1">
        <Label>Role</Label>
        <Select value={role} onValueChange={onChangeRole} disabled={roleDisabled}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CUSTOMER_ADMIN_ASSIGNABLE_ROLES.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

export function ManageUsersPage() {
  const user = useSelector(appSelector.user);
  const orgId = user?.organizationId ?? "";
  const selfId = user?.lassoUserId;
  const isOrgAdmin = useHasAdminFeatureAccess("SELF_SERVE_ADMIN_ENABLED");

  const [roster, setRoster] = useState<AllowedUserInOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  const debouncedUserSearch = useDebounceState(userSearch, 300);
  const [teamPageSize, setTeamPageSize] = useState(DEFAULT_TEAM_PAGE_SIZE);

  const loadRoster = useCallback(async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await AuthProvider.getAdminAllowedUsersInOrg();
      if (!res.success) {
        toast.error(res.error ?? "Could not load users.");
        setRoster([]);
        return;
      }
      setRoster(res.data.extraData?.allowedUsersInOrg ?? []);
    } catch {
      toast.error("Could not load users.");
      setRoster([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void loadRoster();
  }, [loadRoster]);

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addFirst, setAddFirst] = useState("");
  const [addLast, setAddLast] = useState("");
  const [addRole, setAddRole] = useState<string>("user");
  const [addSaving, setAddSaving] = useState(false);

  const [editRow, setEditRow] = useState<AllowedUserInOrg | null>(null);
  const [editFirst, setEditFirst] = useState("");
  const [editLast, setEditLast] = useState("");
  const [editRole, setEditRole] = useState("user");
  const [editSaving, setEditSaving] = useState(false);

  const [deleteRow, setDeleteRow] = useState<AllowedUserInOrg | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  // Role is uneditable when (a) acting user is not an admin (defensive — wrapper should block),
  // (b) the target is the sole remaining admin and is the acting user (no self-demote), or
  // (c) the target is Rebuild staff whose role customer admins can't assign.
  const adminCount = roster.filter((r) => r.role === "admin").length;
  const isSoleAdminSelf =
    !!editRow && editRow.lassoUserId === selfId && editRow.role === "admin" && adminCount <= 1;
  const isTargetRebuildStaff =
    !!editRow && (editRow.role === "internal" || editRow.role === "creator");
  const canEditRole = isOrgAdmin && !isSoleAdminSelf && !isTargetRebuildStaff;

  const openEdit = useCallback((row: AllowedUserInOrg) => {
    setEditRow(row);
    setEditFirst(row.firstName ?? "");
    setEditLast(row.lastName ?? "");
    setEditRole(row.role);
  }, []);

  const teamColumns = useMemo(
    () => buildTeamColumns({ selfId, onEdit: openEdit, onDelete: setDeleteRow }),
    [selfId, openEdit],
  );

  const teamTableRows: ManageTeamTableRow[] = roster
    .filter((row) => !isRebuildStaffUser(row))
    .filter((row) => rosterMatchesQuery(row, debouncedUserSearch))
    .map((r) => ({ ...r, id: r.lassoUserId || r.email }));

  const handleAdd = async () => {
    if (!orgId) return;
    const email = addEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setAddSaving(true);
    try {
      const res = await AuthProvider.editOrganization(
        { organizationId: orgId },
        {
          users: [
            {
              email,
              firstName: addFirst.trim(),
              lastName: addLast.trim(),
              role: addRole,
              photoURL: "",
            },
          ],
        },
      );
      if (!res.success) {
        toast.error(res.error ?? "Could not invite user.");
        return;
      }
      toast.success("Invitation sent.");
      setAddOpen(false);
      setAddEmail("");
      setAddFirst("");
      setAddLast("");
      setAddRole("user");
      await loadRoster();
    } catch {
      toast.error("Could not invite user.");
    } finally {
      setAddSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!orgId || !editRow) return;
    setEditSaving(true);
    try {
      const res = await AuthProvider.updateUserDetails({
        organizationId: orgId,
        lassoUserId: editRow.lassoUserId,
        email: editRow.email,
        role: editRole,
        firstName: editFirst.trim(),
        lastName: editLast.trim(),
        photoURL: editRow.photoURL ?? "",
      });
      if (!res.success) {
        toast.error(res.error ?? "Could not save user.");
        return;
      }
      toast.success("User updated.");
      setEditRow(null);
      await loadRoster();
    } catch {
      toast.error("Could not save user.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!orgId || !deleteRow) return;
    setDeleteSaving(true);
    try {
      const res = await AuthProvider.deleteOrganizationUser(orgId, deleteRow.lassoUserId);
      if (!res.success) {
        toast.error(res.error ?? "Could not remove user.");
        return;
      }
      toast.success("User removed.");
      setDeleteRow(null);
      await loadRoster();
    } catch {
      toast.error("Could not remove user.");
    } finally {
      setDeleteSaving(false);
    }
  };

  const teamTableToolbar = (
    <SettingsTableToolbar>
      <SearchInput
        value={userSearch}
        onSearch={setUserSearch}
        placeholder="Search people…"
        wrapperClassName="min-w-0 flex-1 basis-[12rem] max-w-md shrink"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1 shrink-0 sm:ml-auto"
        onClick={() => setAddOpen(true)}
      >
        <Plus className="size-4" />
        Add user
      </Button>
    </SettingsTableToolbar>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto">
      <div className="flex flex-col gap-3">
        <label htmlFor="org-xn" className="text-foreground text-base font-semibold">
            Team Members
        </label>
        
        {loading ? (
          <div className={cn(SETTINGS_TABLE_WRAPPER, "flex flex-col overflow-hidden p-0")}>
            {teamTableToolbar}
            <DataTable<ManageTeamTableRow>
              columns={teamColumns}
              data={[]}
              embedded
              fluidLayout
              isLoading
              pageSize={teamPageSize}
              onPageSizeChange={setTeamPageSize}
              renderSkeleton={() => <RowSkeleton />}
              emptyState={<span className="text-muted-foreground text-sm">Loading roster…</span>}
            />
          </div>
        ) : roster.length === 0 ? (
          <div className={SETTINGS_TABLE_WRAPPER}>
            <p className="text-muted-foreground p-6 text-sm">No team members yet. Invite someone to get started.</p>
          </div>
        ) : (
          <div className={cn(SETTINGS_TABLE_WRAPPER, "flex flex-col overflow-hidden p-0")}>
            {teamTableToolbar}
            <DataTable<ManageTeamTableRow>
              columns={teamColumns}
              data={teamTableRows}
              embedded
              fluidLayout
              isLoading={false}
              pageSize={teamPageSize}
              onPageSizeChange={setTeamPageSize}
              renderSkeleton={() => <RowSkeleton />}
              emptyState={
                <span className="text-muted-foreground text-sm">
                  No people match your search. Try a different name or email.
                </span>
              }
            />
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
            <DialogDescription>They will receive an email with a sign-in link.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <UserDetailsFields
              idPrefix="add"
              firstName={addFirst}
              lastName={addLast}
              role={addRole}
              onChangeFirstName={setAddFirst}
              onChangeLastName={setAddLast}
              onChangeRole={setAddRole}
            >
              <div className="flex flex-col gap-1">
                <Label htmlFor="add-email">Email</Label>
                <Input id="add-email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} autoComplete="off" />
                <p className="text-muted-foreground text-xs">Email cannot be changed once invite is sent.</p>
              </div>
            </UserDetailsFields>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleAdd()} disabled={addSaving}>
              {addSaving ? "Saving…" : "Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>{editRow?.email}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <UserDetailsFields
              idPrefix="edit"
              firstName={editFirst}
              lastName={editLast}
              role={editRole}
              onChangeFirstName={setEditFirst}
              onChangeLastName={setEditLast}
              onChangeRole={setEditRole}
              roleDisabled={!canEditRole}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditRow(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleSaveEdit()} disabled={editSaving}>
              {editSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRow ? `This will remove access for ${deleteRow.email}. Estimates assigned to them become unassigned.` : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSaving}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="border-destructive/40"
              disabled={deleteSaving}
              onClick={() => void handleDelete()}
            >
              {deleteSaving ? "Removing…" : "Remove"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
